/**
 * BundleCard Component (Phase 25.4)
 *
 * Expandable card that clusters Ring 1 bundled assignments (video + quiz)
 * sharing the same `fanoutGroupId` into a single visual unit.
 *
 * The parent (`ContentTab`) decides whether to render a BundleCard vs a
 * standalone `TrainingCard` based on the runtime rule:
 *   items.filter(i => i.fanoutGroupId === fg).length >= 2  → BundleCard
 * This works regardless of the current `RING_1_INCLUDES_QUIZ` config state,
 * so a mid-flight config flip never produces a half-broken bundle.
 *
 * Behavior:
 *   - Shows a summary header (title, rolled-up progress "N of M complete").
 *   - Tapping the header expands to reveal sub-cards for the video and quiz.
 *   - Sub-card for `materialType='video'`  → navigates to
 *     `/operator/content/{assignmentId}` (TrainingViewer).
 *   - Sub-card for `materialType='quiz'`   → navigates to
 *     `/operator/practice/quiz/:sectionId` (Phase 21.6 quiz player).
 *     When `sectionId` is null (legacy/edge case) the quiz sub-card renders
 *     disabled with a tooltip explaining the missing section reference.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Video,
  Brain,
  Clock,
  AlertTriangle,
  CheckCircle2,
  PlayCircle,
  ChevronDown,
  ChevronUp,
  Layers,
} from 'lucide-react';
import type { TrainingItem } from '../../../hooks/useOperatorPortal';
import { OperatorCard } from './OperatorCard';
import { formatDuration, formatDueDate } from '../utils';
import { cn } from '../../../lib/utils';

interface BundleCardProps {
  /** All TrainingItems that share this bundle's `fanoutGroupId` (>=2 items). */
  items: TrainingItem[];
  index?: number;
}

export function BundleCard({ items, index = 0 }: BundleCardProps) {
  const navigate = useNavigate();
  // Default-expand bundles that have any in-progress item so the operator
  // can continue without an extra tap.
  const hasInProgress = items.some((i) => i.status === 'in_progress');
  const [expanded, setExpanded] = useState(hasInProgress);

  const completedCount = items.filter((i) => i.status === 'completed').length;
  const totalCount = items.length;
  const allComplete = completedCount === totalCount;
  const anyOverdue = items.some((i) => i.isOverdue && i.status !== 'completed');

  // Pick a representative item for the title/section framing. Prefer the
  // video slot since it's the primary teaching material; fall back to the
  // first item if no video is present.
  const videoItem = items.find((i) => i.bundleSlot === 'video' || i.material.materialType === 'video');
  const quizItem = items.find((i) => i.bundleSlot === 'quiz' || i.material.materialType === 'quiz');
  const headerItem = videoItem ?? items[0];

  const sectionLabel = headerItem.sectionTitle ?? headerItem.sopName ?? null;
  const bundleTitle = headerItem.material.title;

  const getHeaderBadge = () => {
    if (anyOverdue) {
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-[11px] font-semibold text-red-700">
          <AlertTriangle size={11} strokeWidth={1.5} />
          Overdue
        </span>
      );
    }
    if (allComplete) {
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">
          <CheckCircle2 size={11} strokeWidth={1.5} />
          Completed
        </span>
      );
    }
    if (hasInProgress) {
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2 py-0.5 text-[11px] font-semibold text-blue-700">
          <PlayCircle size={11} strokeWidth={1.5} />
          In Progress
        </span>
      );
    }
    return (
      <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-semibold text-gray-600">
        Assigned
      </span>
    );
  };

  const progressPct = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

  return (
    <div
      style={{
        animation: `fadeInUp 0.4s ease-out ${index < 5 ? index * 0.06 : 0}s both`,
      }}
    >
      <OperatorCard className="overflow-hidden !p-0">
        {/* Rolled-up progress indicator */}
        <div className="h-1.5 w-full bg-gray-100">
          <div
            className={cn(
              'h-full rounded-full transition-all duration-500',
              allComplete
                ? 'bg-emerald-500'
                : hasInProgress
                  ? 'bg-guild-green'
                  : 'bg-gray-200',
            )}
            style={{ width: `${progressPct}%` }}
          />
        </div>

        {/* Header — tappable to expand/collapse */}
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="w-full p-4 text-left active:bg-gray-50 transition-colors duration-150 focus-visible:ring-2 focus-visible:ring-guild-green focus-visible:ring-offset-2"
          aria-expanded={expanded}
        >
          <div className="flex items-start gap-3">
            <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-guild-mint text-guild-green">
              <Layers size={22} strokeWidth={1.5} />
            </div>

            <div className="min-w-0 flex-1">
              <div className="mb-1.5 flex items-start justify-between gap-2">
                <h3 className="text-base font-semibold text-op-text-primary line-clamp-1">
                  {bundleTitle}
                </h3>
                <div className="flex flex-shrink-0 items-center gap-1.5">
                  {getHeaderBadge()}
                </div>
              </div>

              {sectionLabel && (
                <p className="mb-1 text-xs font-medium text-op-text-tertiary line-clamp-1">
                  {sectionLabel}
                </p>
              )}

              <div className="flex flex-wrap items-center gap-3 text-xs text-op-text-tertiary">
                <span className="font-medium text-guild-green">
                  {completedCount} of {totalCount} complete
                </span>
                {headerItem.dueDate && !allComplete && (
                  <span
                    className={cn(
                      'flex items-center gap-1',
                      anyOverdue ? 'text-red-600 font-medium' : '',
                    )}
                  >
                    {anyOverdue && <AlertTriangle size={13} strokeWidth={1.5} />}
                    {formatDueDate(headerItem.dueDate)}
                  </span>
                )}
                <span className="ml-auto inline-flex items-center gap-1 text-op-text-tertiary">
                  {expanded ? (
                    <ChevronUp size={14} strokeWidth={1.5} />
                  ) : (
                    <ChevronDown size={14} strokeWidth={1.5} />
                  )}
                </span>
              </div>
            </div>
          </div>
        </button>

        {/* Expanded sub-cards */}
        {expanded && (
          <div className="border-t border-gray-100 bg-op-muted/40 p-3 space-y-2">
            {videoItem && (
              <BundleSubCard
                item={videoItem}
                icon={<Video size={18} strokeWidth={1.5} />}
                iconBg="bg-violet-100 text-violet-600"
                typeLabel="Video"
                onClick={() =>
                  navigate(`/operator/content/${videoItem.assignmentId}`)
                }
              />
            )}
            {quizItem && (
              <BundleSubCard
                item={quizItem}
                icon={<Brain size={18} strokeWidth={1.5} />}
                iconBg="bg-cyan-100 text-cyan-600"
                typeLabel="Quiz"
                disabled={!quizItem.sectionId}
                disabledReason={
                  !quizItem.sectionId ? 'Section unavailable' : undefined
                }
                onClick={() => {
                  if (quizItem.sectionId) {
                    navigate(`/operator/practice/quiz/${quizItem.sectionId}`);
                  }
                }}
              />
            )}
            {/* Any remaining items (future Ring 1 bundle extensions) */}
            {items
              .filter((i) => i !== videoItem && i !== quizItem)
              .map((i) => (
                <BundleSubCard
                  key={i.assignmentId}
                  item={i}
                  icon={<Video size={18} strokeWidth={1.5} />}
                  iconBg="bg-gray-100 text-gray-600"
                  typeLabel={i.material.materialType}
                  onClick={() =>
                    navigate(`/operator/content/${i.assignmentId}`)
                  }
                />
              ))}
          </div>
        )}
      </OperatorCard>
    </div>
  );
}

interface BundleSubCardProps {
  item: TrainingItem;
  icon: React.ReactNode;
  iconBg: string;
  typeLabel: string;
  disabled?: boolean;
  disabledReason?: string;
  onClick: () => void;
}

function BundleSubCard({
  item,
  icon,
  iconBg,
  typeLabel,
  disabled,
  disabledReason,
  onClick,
}: BundleSubCardProps) {
  const isCompleted = item.status === 'completed';
  const isInProgress = item.status === 'in_progress';

  const statusBadge = isCompleted ? (
    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">
      <CheckCircle2 size={11} strokeWidth={1.5} />
      Done
    </span>
  ) : isInProgress ? (
    <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2 py-0.5 text-[11px] font-semibold text-blue-700">
      <PlayCircle size={11} strokeWidth={1.5} />
      In Progress
    </span>
  ) : (
    <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-semibold text-gray-600">
      Start
    </span>
  );

  return (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      title={disabledReason}
      className={cn(
        'w-full rounded-xl bg-op-card p-3 text-left ring-1 ring-black/[0.04] transition-transform duration-150 focus-visible:ring-2 focus-visible:ring-guild-green focus-visible:ring-offset-2',
        disabled
          ? 'cursor-not-allowed opacity-60'
          : 'active:scale-[0.98] cursor-pointer',
      )}
    >
      <div className="flex items-center gap-3">
        <div
          className={cn(
            'flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg',
            iconBg,
          )}
        >
          {icon}
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-op-text-primary line-clamp-1">
            {item.material.title}
          </p>
          <div className="flex flex-wrap items-center gap-2 text-[11px] text-op-text-tertiary">
            <span className="font-medium uppercase tracking-wide">
              {typeLabel}
            </span>
            {item.material.duration && item.material.materialType === 'video' && (
              <span className="flex items-center gap-1">
                <Clock size={11} strokeWidth={1.5} />
                {formatDuration(item.material.duration)}
              </span>
            )}
            {disabledReason && <span className="text-red-600">{disabledReason}</span>}
          </div>
        </div>
        <div className="flex-shrink-0">{statusBadge}</div>
      </div>
    </button>
  );
}
